import React from "react";
import { usePolling } from "@/components/croc/hooks";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtUsd, shortAddr } from "@/lib/format";
import api from "@/lib/api";
import { Radio, ArrowUpRight } from "lucide-react";

const ago = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export const MarketingFeed = () => {
  const { data } = usePolling(() => api.mktFeed(), [], 15000);
  const items = data?.items ?? [];

  return (
    <Panel data-testid="marketing-feed">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#39FF14] live-dot" />
          <span className="font-display font-bold text-white">Network Payouts · Live</span>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone="gold" data-testid="mkt-feed-total">{fmtUsd(data?.total_paid_usd)} paid</Pill>
          <Pill tone="emerald" data-testid="mkt-feed-count">{data?.count ?? 0} redemptions</Pill>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm" data-testid="mkt-feed-empty">
          No point redemptions yet — the ledger lights up as downline business converts points to USDT.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
          {items.map((r, i) => (
            <div key={`${r.created_at}-${i}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2.5" data-testid={`mkt-feed-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/25 grid place-items-center shrink-0">
                  <ArrowUpRight className="w-3.5 h-3.5 emerald-text" />
                </div>
                <div className="min-w-0">
                  <div className="font-num text-xs text-white truncate">{shortAddr(r.upline)}</div>
                  <div className="text-[10px] text-muted-foreground">from {shortAddr(r.source)} · {ago(r.created_at)}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-num text-sm emerald-text">+{fmtUsd(r.usd)}</div>
                <div className="text-[10px] gold-text font-num">{Math.round((r.eff_pct || 0) * 100)}%</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Radio className="w-3 h-3 emerald-text" /> Marketing budget converts downline business into USDT in real time.
      </div>
    </Panel>
  );
};
